import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { Button, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';

const { FiPlus, FiLock, FiEdit2, FiCopy, FiTrash2, FiMail } = FiIcons;

const CATEGORY_TABS = [
  { value: 'all', label: 'All' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'appointment', label: 'Appointment' },
  { value: 'invite', label: 'Invite' },
  { value: 'auth_recovery', label: 'Auth recovery' },
  { value: 'auth_otp', label: 'Auth OTP' },
  { value: 'sequence', label: 'Sequence' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'recruit', label: 'Recruit' },
  { value: 'client', label: 'Client' },
];

const CATEGORY_STYLES = {
  onboarding: { dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50' },
  appointment: { dot: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50' },
  invite: { dot: 'bg-indigo-500', text: 'text-indigo-700', bg: 'bg-indigo-50' },
  auth_recovery: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  auth_otp: { dot: 'bg-sky-500', text: 'text-sky-700', bg: 'bg-sky-50' },
  sequence: { dot: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50' },
  campaign: { dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50' },
  recruit: { dot: 'bg-teal-500', text: 'text-teal-700', bg: 'bg-teal-50' },
  client: { dot: 'bg-pink-500', text: 'text-pink-700', bg: 'bg-pink-50' },
};

const CATEGORY_LABELS = {
  auth_recovery: 'Auth recovery',
  auth_otp: 'Auth OTP',
};

function CategoryBadge({ category }) {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.campaign;
  const label = CATEGORY_LABELS[category]
    || (category ? category.charAt(0).toUpperCase() + category.slice(1) : '—');
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {label}
    </span>
  );
}

function EmailPreview({ html }) {
  return (
    <iframe
      srcDoc={html || '<p style="padding:20px;color:#999;font-family:sans-serif;">No HTML content yet.</p>'}
      sandbox="allow-same-origin"
      style={{ width: '100%', height: '600px', border: 'none', borderRadius: '8px' }}
      title="Email Preview"
    />
  );
}

export default function TemplateManager() {
  const { user } = useAuth();
  const canEditSystemTemplate = ['admin', 'super_admin'].includes(user?.role);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.agency_id) {
      setTemplates([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await api.emailTemplates.getAll(user.agency_id);
      setTemplates(rows ?? []);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not load templates',
        message: err?.message || 'Try again.',
      });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredTemplates = useMemo(() => {
    if (categoryFilter === 'all') return templates;
    return templates.filter((t) => t.category === categoryFilter);
  }, [templates, categoryFilter]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  useEffect(() => {
    if (selectedId && !filteredTemplates.some((t) => t.id === selectedId)) {
      setSelectedId(filteredTemplates[0]?.id ?? null);
    }
  }, [filteredTemplates, selectedId]);

  const handleDuplicate = async (template) => {
    if (!user?.agency_id || !user?.id) return;
    setDuplicatingId(template.id);
    try {
      await api.emailTemplates.duplicate(template.id, user.agency_id, user.id);
      await load();
      addToast({ type: 'success', title: 'Template duplicated' });
    } catch (err) {
      addToast({ type: 'error', title: 'Duplicate failed', message: err?.message });
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (template) => {
    if (template.is_system) return;
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return;
    setDeletingId(template.id);
    try {
      await api.emailTemplates.delete(template.id);
      if (selectedId === template.id) setSelectedId(null);
      await load();
      addToast({ type: 'success', title: 'Template deleted' });
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err?.message });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 min-h-[calc(100vh-10rem)]">
      <Card className="w-full lg:w-[300px] lg:shrink-0 flex flex-col overflow-hidden p-0">
        <div className="p-4 border-b border-gray-200 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h1 className="text-lg font-bold text-gray-900">Email Templates</h1>
              <p className="text-xs text-gray-500 mt-0.5">Manage reusable HTML for emails</p>
            </div>
          </div>
          <Link to="/campaigns/templates/new" className="block">
            <Button size="sm" className="w-full">
              <SafeIcon icon={FiPlus} className="mr-2 w-4 h-4" />
              New Template
            </Button>
          </Link>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setCategoryFilter(tab.value)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium border transition-colors ${
                  categoryFilter === tab.value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2">
              <Spinner size="sm" className="text-primary" />
              <span className="text-sm text-gray-500">Loading…</span>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <SafeIcon icon={FiMail} className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No templates in this category</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filteredTemplates.map((template) => {
                const active = selectedId === template.id;
                return (
                  <li key={template.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(template.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        active ? 'bg-primary/5 border-l-2 border-primary' : 'hover:bg-gray-50 border-l-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {template.is_system && (
                          <SafeIcon icon={FiLock} className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-gray-900 truncate">{template.name}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <CategoryBadge category={template.category} />
                            {template.is_system && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                <SafeIcon icon={FiLock} className="w-3 h-3" />
                                System
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Card>

      <Card className="flex-1 min-w-0 flex flex-col overflow-hidden p-0">
        {!selectedTemplate ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16 px-6 text-center">
            <SafeIcon icon={FiMail} className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-700">Select a template to preview</p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              Choose a template from the list to see its subject, description, and HTML preview.
            </p>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="p-4 md:p-6 border-b border-gray-200 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-2 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-900">{selectedTemplate.name}</h2>
                    <CategoryBadge category={selectedTemplate.category} />
                    {selectedTemplate.is_system && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                        <SafeIcon icon={FiLock} className="w-3 h-3" />
                        System
                      </span>
                    )}
                  </div>
                  {selectedTemplate.subject && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-500">Subject:</span>{' '}
                      {selectedTemplate.subject}
                    </p>
                  )}
                  {selectedTemplate.description && (
                    <p className="text-sm text-gray-500">{selectedTemplate.description}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={selectedTemplate.is_system && !canEditSystemTemplate}
                    onClick={() => navigate(`/campaigns/templates/${selectedTemplate.id}/edit`)}
                  >
                    <SafeIcon icon={FiEdit2} className="mr-1.5 w-4 h-4" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    isLoading={duplicatingId === selectedTemplate.id}
                    onClick={() => handleDuplicate(selectedTemplate)}
                  >
                    <SafeIcon icon={FiCopy} className="mr-1.5 w-4 h-4" />
                    Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={selectedTemplate.is_system}
                    isLoading={deletingId === selectedTemplate.id}
                    onClick={() => handleDelete(selectedTemplate)}
                  >
                    <SafeIcon icon={FiTrash2} className="mr-1.5 w-4 h-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex-1 p-4 md:p-6 overflow-auto min-h-0 bg-gray-50">
              <EmailPreview html={selectedTemplate.html_body} />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
